import React, { useState, useEffect, useCallback } from "react";
import { Table, Pagination } from "rsuite";
import { fetchClients } from "../../../api/clientAPI";
import { placeholderImage, isValidUrl } from "../../invalidImage";
import ImageModal from "../../imageModal";
import { handleDeleteClient } from "./deleteClientHandler";
import AdminPageHeader from "../_shared/AdminPageHeader";
import AdminSearchBar from "../_shared/AdminSearchBar";
import AdminCard from "../_shared/AdminCard";
import { EditButton, DeleteButton } from "../_shared/AdminActions";
import ClientEditor from "./ClientEditor";

const { Column, HeaderCell, Cell } = Table;

// ── View modes ────────────────────────────────────────────────
const VIEW = {
  LIST: "list",
  ADD:  "add",
  EDIT: "edit",
};

const ClientTable = () => {
  const [view, setView]               = useState(VIEW.LIST);
  const [editingClient, setEditingClient] = useState(null);

  const [allData, setAllData]         = useState([]);
  const [data, setData]               = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const [page, setPage]               = useState(1);
  const [limit, setLimit]             = useState(10);
  const [searchKeyword, setSearchKeyword] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const clientsData = await fetchClients();
      setAllData(clientsData);
      setData(clientsData);
    } catch (err) {
      console.error("Failed to fetch clients:", err);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const start = (page - 1) * limit;
    setDisplayData(data.slice(start, start + limit));
  }, [page, limit, data]);

  const handleSearch = (value) => {
    setSearchKeyword(value);
    setPage(1);
    if (value) {
      setData(allData.filter((c) => c.name.toLowerCase().includes(value.toLowerCase())));
    } else {
      setData(allData);
    }
  };

  const handleEditorSuccess = () => {
    setView(VIEW.LIST);
    setEditingClient(null);
    fetchData();
  };

  // ── Full-page editor view ─────────────────────────────────
  if (view === VIEW.ADD) {
    return (
      <div className="flex-1 min-h-screen">
        <ClientEditor
          onSuccess={handleEditorSuccess}
          onCancel={() => setView(VIEW.LIST)}
        />
      </div>
    );
  }

  if (view === VIEW.EDIT && editingClient) {
    return (
      <div className="flex-1 min-h-screen">
        <ClientEditor
          client={editingClient}
          onSuccess={handleEditorSuccess}
          onCancel={() => { setView(VIEW.LIST); setEditingClient(null); }}
        />
      </div>
    );
  }

  // ── List view ─────────────────────────────────────────────
  return (
    <div className="flex-1 min-h-screen bg-slate-50 overflow-y-auto">
      <div className="p-6 lg:p-10 max-w-7xl mx-auto">
        <AdminPageHeader
          title="Client Management"
          subtitle="Kelola daftar klien dan mitra Defra"
          badge={data.length}
          actionLabel="➕ Tambah Klien"
          onAction={() => setView(VIEW.ADD)}
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M16 3.13a4 4 0 010 7.75M8 3.13a4 4 0 000 7.75" />
            </svg>
          }
        />

        <AdminCard>
          <AdminSearchBar
            value={searchKeyword}
            onChange={handleSearch}
            placeholder="Cari berdasarkan nama klien..."
          />

          <div className="overflow-hidden rounded-2xl border border-slate-100">
            <Table autoHeight data={displayData} rowHeight={100}>
              <Column width={60} align="center" fixed>
                <HeaderCell className="bg-slate-50 font-semibold text-slate-700">No</HeaderCell>
                <Cell>
                  {(rowData, rowIndex) => (
                    <span className="text-sm font-semibold text-slate-500">
                      {(page - 1) * limit + rowIndex + 1}
                    </span>
                  )}
                </Cell>
              </Column>

              <Column width={120}>
                <HeaderCell className="bg-slate-50 font-semibold text-slate-700">Logo</HeaderCell>
                <Cell>
                  {(rowData) => (
                    <ImageModal imageUrl={isValidUrl(rowData.image) ? rowData.image : placeholderImage} />
                  )}
                </Cell>
              </Column>

              <Column flexGrow={1} minWidth={200}>
                <HeaderCell className="bg-slate-50 font-semibold text-slate-700">Nama</HeaderCell>
                <Cell>
                  {(rowData) => (
                    <span className="font-semibold text-slate-800">{rowData.name}</span>
                  )}
                </Cell>
              </Column>

              <Column width={200}>
                <HeaderCell className="bg-slate-50 font-semibold text-slate-700">Aksi</HeaderCell>
                <Cell>
                  {(rowData) => (
                    <div className="flex items-center gap-2">
                      <EditButton
                        onClick={() => {
                          setEditingClient(rowData);
                          setView(VIEW.EDIT);
                        }}
                      />
                      <DeleteButton onClick={() => handleDeleteClient(rowData.id, data, setData)} />
                    </div>
                  )}
                </Cell>
              </Column>
            </Table>
          </div>

          <div className="flex justify-end mt-5">
            <Pagination
              prev next first last ellipsis boundaryLinks
              maxButtons={5} size="xs"
              layout={["total", "-", "limit", "|", "pager"]}
              total={data.length}
              limitOptions={[5, 10, 20, 50]}
              limit={limit}
              activePage={page}
              onChangePage={setPage}
              onChangeLimit={(l) => { setLimit(l); setPage(1); }}
            />
          </div>
        </AdminCard>
      </div>
    </div>
  );
};

export default ClientTable;
